import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { formatDateTimeWithOffset } from '@/lib/format';
import { isUuid } from '@/lib/validation';
import { StayAccessSection } from '@/pages/access/stay-access-section';
import { Button, DetailPage, DetailRow, PageHeader } from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import {
  bookingDetailQuery,
  bookingRuntime,
  financialReconciliationQuery,
  hasDomainAccess,
  orderDetailQuery,
  paymentDetailQuery,
  type BookingRuntime,
} from './booking-queries';
import { bookingDetailState } from './booking-url';
import { FinancialReconciliationSection } from './financial-reconciliation-section';

const statusLabels = {
  PENDING_PAYMENT: '결제 대기',
  CONFIRMED: '예약 확정',
  CANCELLED: '예약 취소됨',
} as const;

export function BookingDetailPage({ runtime = bookingRuntime }: { runtime?: BookingRuntime }) {
  const { bookingId = '' } = useParams();
  const [params] = useSearchParams();
  const route = bookingDetailState(params);
  const session = useOperatorSession(runtime.session);
  const bookingAllowed = hasDomainAccess(runtime.session, session.generation, 'booking', 2);
  const valid = route.valid && isUuid(bookingId) && bookingId === bookingId.toLowerCase();
  const booking = useQuery(
    bookingDetailQuery(runtime, session.generation, bookingId, valid && bookingAllowed),
  );
  const firstOrderId = booking.data?.order_ids[0] ?? null;
  const selectedOrderId = route.orderId ?? firstOrderId;
  const selectedOrderBelongs =
    selectedOrderId !== null && !!booking.data?.order_ids.includes(selectedOrderId);
  const reconciliationSelection = `${String(session.generation)}:${bookingId}:${selectedOrderId ?? ''}`;
  const [reconciliationRequest, setReconciliationRequest] = useState({
    selection: reconciliationSelection,
    requested: false,
    error: undefined as unknown,
  });
  if (reconciliationRequest.selection !== reconciliationSelection)
    setReconciliationRequest({
      selection: reconciliationSelection,
      requested: false,
      error: undefined,
    });
  const reconciliationRequested =
    reconciliationRequest.selection === reconciliationSelection && reconciliationRequest.requested;
  const queryClient = useQueryClient();
  const orderAllowed = hasDomainAccess(runtime.session, session.generation, 'order', 1);
  const order = useQuery(
    orderDetailQuery(
      runtime,
      session.generation,
      bookingId,
      selectedOrderId ?? '',
      valid && !!booking.data && selectedOrderBelongs && orderAllowed,
    ),
  );
  const paymentBinding = order.data?.payment ?? null;
  const paymentAllowed = hasDomainAccess(runtime.session, session.generation, 'payment', 1);
  const payment = useQuery(
    paymentDetailQuery(
      runtime,
      session.generation,
      bookingId,
      order.data?.id ?? '',
      paymentBinding?.id ?? '',
      valid && !!order.data && !!paymentBinding && paymentAllowed,
    ),
  );
  const reconciliationOptions = financialReconciliationQuery(
    runtime,
    session.generation,
    bookingId,
    selectedOrderId ?? '',
  );
  const reconciliation = useQuery(reconciliationOptions);
  if (session.status !== 'authorized') return null;
  const returnTo = route.returnTo;
  const back = (
    <Button nativeButton={false} role="link" variant="outline" render={<Link to={returnTo} />}>
      예약 목록으로
    </Button>
  );
  const detailUrl = new URLSearchParams(params);
  detailUrl.set('returnTo', returnTo);
  const paymentMatchesOrder =
    !!payment.data &&
    !!order.data &&
    !!paymentBinding &&
    payment.data.id === paymentBinding.id &&
    payment.data.payment_attempt_id === paymentBinding.payment_attempt_id &&
    paymentBinding.payment_attempt_id === paymentBinding.attempt_id &&
    payment.data.order?.id === order.data.id &&
    payment.data.order.payment_id === payment.data.id &&
    payment.data.order.payment_attempt_id === paymentBinding.payment_attempt_id &&
    payment.data.order.attempt_id === paymentBinding.attempt_id;
  const paymentQueryDenied =
    session.deniedDomains.includes('payment') ||
    (!!payment.error && isApiError(payment.error) && payment.error.status === 403);

  const frame = (content: ReactNode) => (
    <DetailPage className="min-h-0 flex-1 overflow-y-auto">
      <ShellHeader>
        <PageHeader
          title="개인 예약 상세"
          description="예약 기간과 상태, 연결된 주문·결제 및 투숙 정보를 확인합니다."
          className="items-start sm:items-center"
        />
      </ShellHeader>
      <DetailPage.Body className="min-h-0">
        {back}
        {content}
      </DetailPage.Body>
    </DetailPage>
  );

  if (!valid)
    return frame(
      <p role="alert" className="body-sm">
        예약 주소가 올바르지 않습니다. 목록에서 예약을 다시 선택해 주세요.
      </p>,
    );
  if (!bookingAllowed)
    return frame(
      <p role="alert" className="body-sm">
        현재 예약 조회 권한이 없습니다. 예약 상세와 하위 패널을 표시하지 않습니다.
      </p>,
    );
  if (booking.status === 'pending')
    return frame(
      <p role="status" className="body-sm">
        예약 상세를 조회하고 있습니다.
      </p>,
    );
  if (booking.status === 'error')
    return frame(
      <div role="alert" className="space-y-2">
        <p className="body-sm">
          {isApiError(booking.error) && booking.error.status === 404
            ? '예약을 찾을 수 없습니다.'
            : '예약 상세를 불러오지 못했습니다.'}
        </p>
        <Button variant="outline" onClick={() => void booking.refetch()}>
          다시 조회
        </Button>
      </div>,
    );
  const bookingData = booking.data;
  return frame(
    <>
      <DetailPage.Section title="예약 상태와 기간">
        <dl className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <DetailRow label="예약 상태" value={statusLabels[bookingData.status]} />
          <DetailRow
            label="저장 이용 기간"
            value={`${formatDateTimeWithOffset(bookingData.start_at)} – ${formatDateTimeWithOffset(bookingData.end_at)}`}
          />
        </dl>
        <details className="body-sm mt-3">
          <summary className="cursor-pointer">
            예약 식별·저장 정보 · {bookingId.slice(0, 8)}
          </summary>
          <dl className="mt-2 grid gap-3 sm:grid-cols-2">
            <DetailRow label="예약 번호" value={bookingData.booking_id} />
            <DetailRow label="Property ID" value={bookingData.property_id} />
            <DetailRow label="Room ID" value={bookingData.room_id} />
            <DetailRow label="Slot ID" value={bookingData.slot_id} />
            <DetailRow label="최초 저장 시각" value={bookingData.created_at} />
            <DetailRow label="마지막 변경 시각" value={bookingData.updated_at} />
          </dl>
        </details>
      </DetailPage.Section>
      <DetailPage.Section
        title="연결된 주문과 결제"
        description="예약에 연결된 주문과 결제의 저장 상태를 각각 확인합니다."
      >
        {bookingData.order_ids.length === 0 ? (
          <p className="body-sm">연결된 주문이 없습니다.</p>
        ) : (
          <>
            <p className="body-sm">확인할 주문을 선택하세요.</p>
            <ul className="space-y-1 py-2">
              {bookingData.order_ids.map((orderId) => {
                const next = new URLSearchParams(detailUrl);
                next.set('order_id', orderId);
                return (
                  <li key={orderId}>
                    <Link
                      className="text-foreground underline-offset-4 hover:underline"
                      title={orderId}
                      to={`/contracts/${bookingData.booking_id}?${next}`}
                    >
                      주문 {orderId.slice(0, 8)}
                    </Link>
                  </li>
                );
              })}
            </ul>
            {selectedOrderBelongs ? (
              orderAllowed ? (
                order.status === 'pending' ? (
                  <p role="status" className="body-sm">
                    주문 저장 상태를 조회하고 있습니다.
                  </p>
                ) : order.status === 'error' ? (
                  <div role="alert" className="space-y-2">
                    <p className="body-sm">
                      {isApiError(order.error) && order.error.status === 403
                        ? '현재 주문 조회 권한이 없습니다.'
                        : '주문 상세를 불러오지 못했습니다.'}
                    </p>
                    <Button variant="outline" onClick={() => void order.refetch()}>
                      주문 다시 조회
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 border-t pt-4">
                    <div className="grid min-w-0 gap-3 xl:grid-cols-2">
                      <section
                        aria-label="주문 저장 상태"
                        className="min-w-0 rounded-md border p-4"
                      >
                        <h3 className="title-sm mb-3">주문 저장 상태</h3>
                        <dl className="grid gap-2">
                          <DetailRow
                            label="주문 번호"
                            value={
                              <details>
                                <summary className="cursor-pointer">
                                  {order.data.id.slice(0, 8)} · 식별 정보
                                </summary>
                                <span className="break-all select-text">{order.data.id}</span>
                              </details>
                            }
                          />
                          <DetailRow label="주문 상태" value={order.data.status} />
                          <DetailRow
                            orientation="column"
                            label="저장 결제 의무 금액"
                            value={`${String(order.data.amount_minor)} ${order.data.currency} (통화 최소 단위)`}
                          />
                          <DetailRow
                            orientation="column"
                            label="저장된 환불 누계"
                            value={`${String(order.data.refunded_minor)} ${order.data.currency} (통화 최소 단위)`}
                          />
                          <DetailRow
                            orientation="column"
                            label="주문 결제 시도 상태"
                            value={order.data.payment_attempt_state ?? '저장 상태 없음'}
                          />
                          <DetailRow
                            orientation="column"
                            label="상품 확정 결과"
                            value={order.data.confirmation_result ?? '저장 결과 없음'}
                          />
                        </dl>
                        {order.data.refund_requests.length > 0 && (
                          <ul className="mt-3 space-y-2">
                            {order.data.refund_requests.map((refund) => (
                              <li key={refund.request_operation_id}>
                                환불 요청 상태 {refund.status} · 요청 저장액{' '}
                                {String(refund.amount_minor)} {refund.currency} (통화 최소 단위) ·
                                기록된 완료액 {String(refund.completed_minor)} {refund.currency}{' '}
                                (통화 최소 단위)
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                      <section
                        aria-label="결제 저장 상태"
                        className="min-w-0 rounded-md border p-4"
                      >
                        <h3 className="title-sm mb-3">결제 저장 상태</h3>
                        {order.data.payment === null ? (
                          <p className="body-sm">
                            현재 주문 응답에 정확히 연결된 결제 ID가 없습니다.
                            {order.data.payment_attempt_id !== null &&
                              ` 저장된 결제 시도 ID는 ${order.data.payment_attempt_id}입니다.`}
                          </p>
                        ) : paymentQueryDenied || !paymentAllowed ? (
                          <p className="body-sm" role="status">
                            현재 결제 조회 권한이 없어 이 패널을 제한했습니다. 주문이 반환한
                            exact-attempt Payment ID: {order.data.payment.id}
                          </p>
                        ) : payment.status === 'pending' ? (
                          <p className="body-sm" role="status">
                            연결된 결제 저장 상태를 조회하고 있습니다.
                          </p>
                        ) : payment.status === 'error' ? (
                          <div role="alert" className="space-y-2">
                            <p className="body-sm">
                              {isApiError(payment.error) && payment.error.status === 403
                                ? '현재 결제 조회 권한이 없어 이 패널을 제한했습니다.'
                                : '결제 상세를 불러오지 못했습니다.'}
                            </p>
                            <Button variant="outline" onClick={() => void payment.refetch()}>
                              결제 다시 조회
                            </Button>
                          </div>
                        ) : paymentMatchesOrder ? (
                          <dl className="grid gap-2">
                            <DetailRow
                              label="Payment ID"
                              value={
                                <details>
                                  <summary className="cursor-pointer">
                                    {payment.data.id.slice(0, 8)} · 식별 정보
                                  </summary>
                                  <span className="break-all select-text">{payment.data.id}</span>
                                </details>
                              }
                            />
                            <DetailRow label="결제 상태" value={payment.data.status} />
                            <DetailRow
                              orientation="column"
                              label="저장 결제 금액"
                              value={`${String(payment.data.amount_minor)} ${payment.data.currency} (통화 최소 단위)`}
                            />
                            <DetailRow
                              orientation="column"
                              label="매입 사실 저장 여부"
                              value={payment.data.captured ? '기록됨' : '기록되지 않음'}
                            />
                          </dl>
                        ) : (
                          <p role="alert" className="body-sm">
                            조회한 결제가 현재 주문에 정확히 연결된 결제 시도와 일치하지 않아 상세를
                            숨겼습니다.
                          </p>
                        )}
                      </section>
                    </div>
                    <FinancialReconciliationSection
                      result={reconciliation.data}
                      fetching={reconciliation.isFetching}
                      error={reconciliation.error ?? reconciliationRequest.error}
                      requested={reconciliationRequested}
                      canRead={paymentAllowed}
                      onRead={() => {
                        setReconciliationRequest({
                          selection: reconciliationSelection,
                          requested: true,
                          error: undefined,
                        });
                        void reconciliation.refetch().then((response) => {
                          if (isApiError(response.error) && response.error.status === 403) {
                            queryClient.removeQueries({
                              queryKey: reconciliationOptions.queryKey,
                              exact: true,
                            });
                            setReconciliationRequest({
                              selection: reconciliationSelection,
                              requested: true,
                              error: response.error,
                            });
                          }
                        });
                      }}
                    />
                  </div>
                )
              ) : (
                <p role="status" className="body-sm">
                  현재 주문 조회 권한이 없어 주문 상태와 결제 연결 상세를 표시할 수 없습니다.
                </p>
              )
            ) : (
              <p role="alert" className="body-sm">
                선택한 주문 번호는 현재 예약 응답의 연결 목록에 없습니다.
              </p>
            )}
          </>
        )}
      </DetailPage.Section>
      <DetailPage.Section title="연결된 투숙과 출입 기록">
        {bookingData.stay_id !== null && (
          <p className="body-sm">연결된 투숙 번호: {bookingData.stay_id}</p>
        )}
        <StayAccessSection
          key={bookingData.booking_id}
          stayId={bookingData.stay_id}
          runtime={runtime}
        />
      </DetailPage.Section>
    </>,
  );
}
